import type { CrudModel, CrudObject } from 'src/CrudManager/Core/types/AdvancedCrudManager'
import { showErrorNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { Notification } from '../types'
import type { ActionContext, ActionHandler, ActionHandlerModule, ActionHandlers, ActionRegistry, ActionRegistryEntry, ActionResult } from '../types/actionTypes'

// Global registry instance
const actionRegistry = ref<Map<string, ActionRegistryEntry>>(new Map())
const isInitialized = ref(false)

/**
 * Auto-discovery of standalone action handlers
 */
const discoverStandaloneActions = async (): Promise<void> => {
  try {
    // Use import.meta.glob to discover action handler files
    const actionModules = import.meta.glob('src/notificationActions/*.ts', { eager: false })

    for (const [path, moduleImporter] of Object.entries(actionModules)) {
      try {
        const module = (await moduleImporter()) as ActionHandlerModule

        // Handle default export
        if (module.default) {
          registerHandlers(module.default, 'standalone')
        }

        // Handle named exports that are functions
        Object.entries(module).forEach(([exportName, exportValue]) => {
          if (exportName !== 'default' && typeof exportValue === 'function') {
            register(exportName, exportValue as ActionHandler, 'standalone')
          }
        })

        console.log(`Loaded notification actions from: ${path}`)
      } catch (error) {
        console.warn(`Failed to load notification actions from ${path}:`, error)
      }
    }
  } catch (error) {
    console.warn('Failed to discover standalone notification actions:', error)
  }
}

/**
 * Auto-discovery of CrudModel notification actions
 */
const discoverCrudModelActions = async (): Promise<void> => {
  try {
    // Import all CrudModel configurations
    const crudModules = import.meta.glob('/src/CrudManager/CrudModels/*/index.ts', { eager: false })

    for (const [path, moduleImporter] of Object.entries(crudModules)) {
      try {
        const module = await moduleImporter()
        const crudModel = module.default as CrudModel<CrudObject>

        if (crudModel && crudModel.notificationActions) {
          registerHandlers(crudModel.notificationActions, 'crudmodel', crudModel.name)
        }

        // Auto-generate standard CRUD actions
        generateCrudActions(crudModel)
      } catch (error) {
        console.warn(`Failed to load CrudModel actions from ${path}:`, error)
      }
    }
  } catch (error) {
    console.warn('Failed to discover CrudModel notification actions:', error)
  }
}

/**
 * Generate standard CRUD actions for a model
 */
const generateCrudActions = (crudModel: CrudModel<CrudObject>): void => {
  const modelName = crudModel.name

  // Generate view action
  const viewAction: ActionHandler = async (context: ActionContext): Promise<ActionResult> => {
    const { notification, router } = context
    if (notification.model_id) {
      const route = `/editor/${modelName}/${notification.model_id}`
      await router.push(route)
      return { success: true, message: `Opened ${crudModel.getName?.({} as any) || modelName}` }
    }
    return { success: false, error: 'No model ID available' }
  }

  // Generate edit action (same as view for now)
  const editAction: ActionHandler = viewAction

  // Generate list action
  const listAction: ActionHandler = async (context: ActionContext): Promise<ActionResult> => {
    const { router } = context
    const route = `/list/${modelName}`
    await router.push(route)
    return { success: true, message: `Opened ${modelName} list` }
  }

  // Generate create action
  const createAction: ActionHandler = async (context: ActionContext): Promise<ActionResult> => {
    const { router } = context
    const route = `/creator/${modelName}`
    await router.push(route)
    return { success: true, message: `Opened ${modelName} creator` }
  }

  // Register generated actions with model prefix
  register(`${modelName}.view`, viewAction, 'crudmodel', modelName)
  register(`${modelName}.edit`, editAction, 'crudmodel', modelName)
  register(`${modelName}.list`, listAction, 'crudmodel', modelName)
  register(`${modelName}.create`, createAction, 'crudmodel', modelName)

  // Also register generic actions that can work with any model
  register('model.view', viewAction, 'crudmodel', modelName)
  register('model.edit', editAction, 'crudmodel', modelName)
  register('model.list', listAction, 'crudmodel', modelName)
  register('model.create', createAction, 'crudmodel', modelName)
}

/**
 * Register a single action handler
 */
const register = (actionName: string, handler: ActionHandler, source: 'standalone' | 'crudmodel' = 'standalone', modelName?: string): void => {
  actionRegistry.value.set(actionName, {
    handler,
    source,
    modelName,
    loaded: true,
  })
}

/**
 * Register multiple action handlers
 */
const registerHandlers = (handlers: ActionHandlers, source: 'standalone' | 'crudmodel' = 'standalone', modelName?: string): void => {
  Object.entries(handlers).forEach(([actionName, handler]) => {
    register(actionName, handler, source, modelName)
  })
}

/**
 * Get an action handler
 */
const get = (actionName: string): ActionHandler | undefined => {
  const entry = actionRegistry.value.get(actionName)
  return entry?.handler
}

/**
 * Check if an action exists
 */
const has = (actionName: string): boolean => {
  return actionRegistry.value.has(actionName)
}

/**
 * Get all registered action names
 */
const getActionNames = (): string[] => {
  return Array.from(actionRegistry.value.keys())
}

/**
 * Clear all registered actions
 */
const clear = (): void => {
  actionRegistry.value.clear()
  isInitialized.value = false
}

/**
 * Initialize the action registry
 */
const initialize = async (): Promise<void> => {
  if (isInitialized.value) return

  try {
    console.log('Initializing notification action registry...')

    // Discover and register all actions
    await Promise.all([discoverStandaloneActions(), discoverCrudModelActions()])

    isInitialized.value = true
    console.log(`Notification action registry initialized with ${actionRegistry.value.size} actions`)
  } catch (error) {
    console.error('Failed to initialize notification action registry:', error)
    showErrorNotification('Failed to initialize notification actions')
  }
}

/**
 * Execute an action with proper context
 */
const executeAction = async (actionName: string, notification: Notification, actionData?: Record<string, any>): Promise<ActionResult> => {
  const router = useRouter()

  // Ensure registry is initialized
  if (!isInitialized.value) {
    await initialize()
  }

  const handler = get(actionName)
  if (!handler) {
    const error = `Action handler '${actionName}' not found`
    console.warn(error, 'Available actions:', getActionNames())
    return { success: false, error }
  }

  const context: ActionContext = {
    notification,
    router,
    actionData,
    // TODO: Add crudModel and modelInstance when available
  }

  try {
    const result = await handler(context)
    return result
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Action execution failed'
    console.error(`Action '${actionName}' failed:`, error)
    return { success: false, error: errorMessage }
  }
}

/**
 * Main composable for notification action registry
 */
export function useActionRegistry() {
  return {
    // State
    isInitialized: computed(() => isInitialized.value),
    actionCount: computed(() => actionRegistry.value.size),

    // Registry methods
    register,
    registerHandlers,
    get,
    has,
    getActionNames,
    clear,
    initialize,

    // Execution
    executeAction,

    // Registry interface
    registry: {
      register,
      registerHandlers,
      get,
      has,
      getActionNames,
      clear,
    } as ActionRegistry,
  }
}
